import React from 'react';
import { Button, Typography, Alert } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  setFrontDamagedParts,
  setRearDamagedParts,
  setSideDamagedParts,
  FrontEndDamage,
  RearEndDamage,
  SideDamage,
  allFrontEndDamage,
  allRearEndDamage,
  allSideDamage,
} from '../../../redux/reducers/damagedPartsSlice';
import './DamagedPartsStep.css';

const { Title } = Typography;

interface DamagedPartsStepProps {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const DamagedPartsStep: React.FC<DamagedPartsStepProps> = ({
  onNext,
  onPrevious,
}) => {
  const dispatch = useAppDispatch();
  const damageLocations = useAppSelector((state) => state.damageLocations);
  const damagedParts = useAppSelector((state) => state.damagedParts);

  const { hasFrontEndDamage, hasRearEndDamage, hasSideDamage } = damageLocations;

  function renderDamagePartSection(
    title: string,
    parts: string[],
    damagedMap: Record<string, boolean>,
    onToggle: (part: string) => void
  ) {
    return (
      <div className="parts-section" key={title}>
        <div className="parts-section-title">{title.toUpperCase()}</div>
        {parts.map((part) => (
          <div
            key={part}
            className={`parts-row${damagedMap[part] ? ' parts-row-selected' : ''}`}
            onClick={() => onToggle(part)}
          >
            <span>{part}</span>
            {damagedMap[part] && <CheckOutlined className="parts-check-icon" />}
          </div>
        ))}
      </div>
    );
  }

  if (!hasFrontEndDamage && !hasSideDamage && !hasRearEndDamage) {
    return (
      <div className="step-container damaged-parts-container">
        <Title level={5} style={{ textAlign: 'center' }}>Damaged Parts</Title>
        <Alert
          message="No damage locations selected"
          description="Please go back and select damage locations to see relevant parts"
          type="info"
          showIcon
        />
        <div className="step-actions" style={{ marginTop: 40 }}>
          <Button size="large" onClick={onPrevious} style={{ color: '#FF9902', fontWeight: 'bold' }}>PREVIOUS</Button>
          <Button type="primary" size="large" onClick={onNext} style={{ fontWeight: 'bold' }}>CONTINUE</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="step-container damaged-parts-container">
      <Title level={5} style={{ textAlign: 'center' }}>Damaged Parts</Title>

      <div className="damaged-parts-step">
        {hasFrontEndDamage &&
          renderDamagePartSection(
            'Front Damage',
            allFrontEndDamage(),
            damagedParts.frontDamagedParts,
            (part) =>
              dispatch(
                setFrontDamagedParts({
                  type: part as FrontEndDamage,
                  damaged: !damagedParts.frontDamagedParts[part as FrontEndDamage],
                })
              )
          )}
        {hasSideDamage &&
          renderDamagePartSection(
            'Side Damage',
            allSideDamage(),
            damagedParts.sideDamagedParts,
            (part) =>
              dispatch(
                setSideDamagedParts({
                  type: part as SideDamage,
                  damaged: !damagedParts.sideDamagedParts[part as SideDamage],
                })
              )
          )}
        {hasRearEndDamage &&
          renderDamagePartSection(
            'Rear Damage',
            allRearEndDamage(),
            damagedParts.rearDamagedParts,
            (part) =>
              dispatch(
                setRearDamagedParts({
                  type: part as RearEndDamage,
                  damaged: !damagedParts.rearDamagedParts[part as RearEndDamage],
                })
              )
          )}
      </div>

      <div className="step-actions" style={{ marginTop: 40 }}>
        <Button size="large" onClick={onPrevious} style={{ color: '#FF9902', fontWeight: 'bold' }}>PREVIOUS</Button>
        <Button type="primary" size="large" onClick={onNext} style={{ fontWeight: 'bold' }}>CONTINUE</Button>
      </div>
    </div>
  );
};

export default DamagedPartsStep;
